<script lang="ts">
  import * as Avatar from '$lib/components/ui/avatar/index'
  import * as Collapsible from '$lib/components/ui/collapsible/index'
  import * as DropdownMenu from '$lib/components/ui/dropdown-menu/index.js'

  import { route, goto } from '@mateothegreat/svelte5-router'
  import { currentPath, servers } from '$lib/store'
  import { scanLibraries } from '$lib/apis/library/api'
  import { trackOperation, operationTitle } from '$lib/operations.svelte'
  import { formatServerName, serverHash } from '$lib/utils'
  import { openGrantAccess } from '$lib/grant-access.svelte'

  import { cn } from '$lib/utils.js'
  import ChevronDown from '@lucide/svelte/icons/chevron-down'
  import EllipsisVertical from '@lucide/svelte/icons/ellipsis-vertical'
  import Plus from '@lucide/svelte/icons/plus'
  import Unplug from '@lucide/svelte/icons/unplug'

  let { item, props, openCreateNewLibrary, disconnected = false } = $props()
  let mouseFocus = $state(false)
  let menuOpen = $state(false)
  let scanning = $state(false)

  const focus = $derived(mouseFocus || menuOpen)
  const displayName = $derived(formatServerName(item.name, item.uid))
  const tag = $derived(serverHash(item.uid))
  const serverLabel = $derived(item.name?.trim() || 'Untitled server')
  const isOwner = $derived(String(item.membership ?? '').toUpperCase() === 'OWNER')

  function getInitials(name: string) {
    return name
      .split('-')
      .map((e) => e[0])
      .join('-')
  }

  const active = $derived($currentPath.includes(item.uid))

  async function onScanLibraries() {
    if (scanning) return
    scanning = true
    try {
      const server = $servers[item.uid]
      const data = await scanLibraries(server)
      trackOperation({
        server,
        operation: data?.operation ?? {},
        title: operationTitle(data?.operation?.type, 'Scanning libraries'),
        description: displayName,
      })
    } catch (err) {
      console.error('scan libraries failed', err)
    } finally {
      scanning = false
    }
  }
</script>

<div
  class="relative flex w-full items-center gap-0.5"
  onmouseenter={() => (mouseFocus = true)}
  onmouseleave={() => (mouseFocus = false)}
>
  <Collapsible.Trigger
    class="flex size-5 shrink-0 items-center justify-center rounded-md text-latte-text/45 transition-colors
      hover:bg-latte-surface1/50 hover:text-latte-text
      dark:text-macchiato-text/45 dark:hover:bg-macchiato-surface1/50 dark:hover:text-macchiato-text"
  >
    <ChevronDown
      class="size-3.5 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180"
    />
  </Collapsible.Trigger>

  <a
    href="/media/{item.uid}"
    {...props}
    class={cn('flex min-w-0 flex-1 items-center gap-2 pe-14', props?.class)}
    use:route
  >
    <Avatar.Root
      class="relative size-8 shrink-0 ring-1 ring-latte-surface1/80 transition-shadow dark:ring-macchiato-surface1/80
        {active
          ? 'ring-latte-green/80 ring-offset-1 ring-offset-transparent dark:ring-macchiato-green/80'
          : ''}
        {disconnected ? 'opacity-70 ring-latte-red/50 dark:ring-macchiato-red/50' : ''}"
    >
      <Avatar.Fallback
        class="bg-latte-mantle text-[0.65rem] font-medium tracking-wide text-latte-text
          dark:bg-macchiato-mantle dark:text-macchiato-text"
      >
        {getInitials(serverLabel)}
      </Avatar.Fallback>
    </Avatar.Root>
    <span class="min-w-0 truncate text-sm leading-tight text-latte-text dark:text-macchiato-text">
      <span
        class="font-medium {active && !disconnected
          ? 'text-latte-green dark:text-macchiato-green'
          : ''}
          {disconnected ? 'text-latte-text/70 dark:text-macchiato-text/70' : ''}"
      >
        {serverLabel}
      </span>
      {#if tag}
        <span class="ml-1 font-normal text-latte-text/40 dark:text-macchiato-text/40">#{tag}</span>
      {/if}
    </span>
    {#if disconnected}
      <Unplug
        class="size-3.5 shrink-0 text-latte-red dark:text-macchiato-red"
        strokeWidth={2}
        aria-label="Server unreachable"
      />
    {/if}
  </a>

  <div
    class="absolute end-0.5 top-1/2 z-10 flex -translate-y-1/2 items-center gap-0.5 transition-opacity duration-150
      {focus ? 'opacity-100' : 'pointer-events-none opacity-0'}"
  >
    <button
      type="button"
      class="rounded-md p-1 text-latte-text/55 hover:bg-latte-surface1/50 hover:text-latte-text
        dark:text-macchiato-text/55 dark:hover:bg-macchiato-surface1/50 dark:hover:text-macchiato-text"
      onclick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        openCreateNewLibrary(item.uid)
      }}
    >
      <Plus class="size-3.5" />
      <span class="sr-only">Add library</span>
    </button>

    <DropdownMenu.Root bind:open={menuOpen}>
      <DropdownMenu.Trigger
        class="rounded-md p-1 text-latte-text/55 hover:bg-latte-surface1/50 hover:text-latte-text
          dark:text-macchiato-text/55 dark:hover:bg-macchiato-surface1/50 dark:hover:text-macchiato-text"
        onclick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <EllipsisVertical class="size-3.5" />
        <span class="sr-only">Server options</span>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content class="w-48 opacity-95" align="start">
        <DropdownMenu.Group>
          {#if isOwner}
            <DropdownMenu.Item
              onclick={(e) => {
                e.preventDefault()
                openGrantAccess(item.uid, item.name ?? '')
              }}
            >
              Grant Access
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
          {/if}
          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger>Manage Server</DropdownMenu.SubTrigger>
            <DropdownMenu.SubContent>
              <DropdownMenu.Item
                disabled={scanning}
                onclick={(e) => {
                  e.preventDefault()
                  onScanLibraries()
                }}
              >
                {scanning ? 'Scanning…' : 'Scan Libraries'}
              </DropdownMenu.Item>
              <DropdownMenu.Item>Refresh Metadata</DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item
                onclick={(e) => {
                  e.preventDefault()
                  goto(`/media/${item.uid}/settings`)
                }}
              >
                Settings
              </DropdownMenu.Item>
            </DropdownMenu.SubContent>
          </DropdownMenu.Sub>
        </DropdownMenu.Group>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  </div>
</div>
